import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useMediaQuery } from 'react-responsive';

// Palette de couleurs Corporate Premium (Dark Gray & Beige)
const colors = {
  bgDark: '#121214',
  bgMedium: '#1C1C1F',
  bgLight: '#252529',
  beigeLight: '#F5EFEB',
  beigeMain: '#D4C5B0',
  beigeDark: '#A3947F',
  textGray: '#A0A0A5',
  border: 'rgba(212, 197, 176, 0.15)',
};

const DroitEnvironnement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);
  const [showStats, setShowStats] = useState(false);
  
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  // Retrait des couleurs individuelles pour unifier avec le thème Corporate
  const services = [
    {
      id: 1,
      title: 'Autorisations Environnementales',
      description: 'Obtention et gestion des autorisations requises pour les projets industriels et énergétiques.',
      icon: '📋',
      details: [
        'Permis environnementaux',
        'Autorisations spéciales',
        'Déclarations administratives',
        'Suivi des obligations'
      ]
    },
    {
      id: 2,
      title: 'Études d\'Impact',
      description: 'Élaboration et validation des études d\'impact environnemental selon les normes internationales.',
      icon: '🔍',
      details: [
        'Analyse d\'impact environnemental',
        'Études de faisabilité',
        'Évaluation des risques',
        'Consultation publique'
      ]
    },
    {
      id: 3,
      title: 'Hydrocarbures',
      description: 'Conseil en réglementation pétrolière et gazière, structuration de contrats et concessions.',
      icon: '🛢️',
      details: [
        'Contrats d\'exploration',
        'Concessions minières',
        'Réglementation offshore',
        'Transport et stockage'
      ]
    },
    {
      id: 4,
      title: 'Contentieux',
      description: 'Représentation dans les litiges environnementaux et défense contre les sanctions administratives.',
      icon: '⚖️',
      details: [
        'Litiges administratifs',
        'Recours contentieux',
        'Négociations amiables',
        'Expertises judiciaires'
      ]
    }
  ];

  const expertiseAreas = [
    {
      title: 'Conformité Réglementaire',
      points: [
        'Normes nationales et internationales',
        'Audits environnementaux',
        'Reporting et transparence',
        'Certifications vertes'
      ],
      icon: '📊',
      stats: '98%'
    },
    {
      title: 'Développement Durable',
      points: [
        'Politiques RSE',
        'Gestion des ressources',
        'Économie circulaire',
        'Biodiversité'
      ],
      icon: '🌱',
      stats: '250+'
    },
    {
      title: 'Risques Industriels',
      points: [
        'Prévention des pollutions',
        'Gestion des déchets',
        'Sécurité des sites',
        'Plans d\'urgence'
      ],
      icon: '⚠️',
      stats: '15+'
    }
  ];

  // Calcul des styles responsives
  const computedStyles = useMemo(() => {
    return {
      section: {
        position: 'relative',
        background: `linear-gradient(180deg, ${colors.bgDark} 0%, #17171A 50%, ${colors.bgDark} 100%)`,
        color: colors.beigeLight,
        minHeight: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      },
      
      animatedBackground: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 1, overflow: 'hidden',
      },
      
      noiseOverlay: {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.04'/%3E%3C/svg%3E")`,
        pointerEvents: 'none', zIndex: 1,
      },
      
      gradientSphere: {
        position: 'absolute', top: '10%', right: '-10%',
        width: '50vw', height: '50vw',
        background: `radial-gradient(circle, rgba(212, 197, 176, 0.03) 0%, transparent 60%)`,
        filter: 'blur(80px)',
      },
      
      hero: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '6rem 1.5rem' : isTablet ? '8rem 3rem' : '10rem 5%',
        maxWidth: '1440px', margin: '0 auto',
      },
      
      heroContent: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr',
        gap: isMobile ? '3rem' : '5rem',
        alignItems: 'center',
      },
      
      titleLine: {
        height: '1px',
        background: `linear-gradient(90deg, ${colors.beigeMain}, transparent)`,
        marginBottom: isMobile ? '1.5rem' : '2rem',
        width: isMobile ? '60px' : '100px',
      },
      
      mainTitle: {
        fontSize: isMobile ? '2.3rem' : isTablet ? '2.8rem' : '3.8rem',
        lineHeight: 1.1,
        fontWeight: 300,
        marginBottom: isMobile ? '1rem' : '1.5rem',
        color: colors.beigeLight,
        letterSpacing: '-1px',
      },
      
      titleHighlight: {
        color: colors.beigeMain,
        fontStyle: 'italic',
      },
      
      subtitle: {
        fontSize: isMobile ? '1.1rem' : isTablet ? '1.3rem' : '1.6rem',
        color: colors.textGray,
        fontWeight: 300,
        display: 'block',
        marginTop: isMobile ? '0.5rem' : '1rem',
      },
      
      heroTextWrapper: {
        display: 'flex', flexDirection: 'column', gap: '1rem',
        marginBottom: '3rem', marginTop: '2rem',
      },
      
      textCard: {
        background: colors.bgMedium,
        padding: '1.2rem',
        borderLeft: `2px solid ${colors.beigeDark}`,
        display: 'flex', gap: '1rem', alignItems: 'flex-start',
      },
      
      cardIcon: {
        fontSize: '1.5rem', flexShrink: 0,
        filter: 'grayscale(100%) sepia(100%) hue-rotate(5deg) saturate(300%) brightness(0.9)', // Rend l'emoji "Bronze/Or"
      },
      
      heroText: {
        fontSize: '1rem', lineHeight: 1.7, color: colors.textGray,
        margin: 0, fontWeight: 300,
      },
      
      statsContainer: {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'center',
        padding: '2rem 0',
        borderTop: `1px solid ${colors.border}`,
        borderBottom: `1px solid ${colors.border}`,
      },
      
      statItem: {
        textAlign: isMobile ? 'center' : 'left', flex: 1,
        padding: isMobile ? '1rem 0' : '0 2rem',
      },
      
      statNumber: {
        fontSize: isMobile ? '2rem' : '2.5rem',
        fontWeight: 300, color: colors.beigeMain,
        fontFamily: "'Playfair Display', serif",
        marginBottom: '0.2rem',
      },
      
      statLabel: {
        fontSize: '0.8rem', color: colors.textGray, textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      statDivider: {
        width: isMobile ? '50%' : '1px',
        height: isMobile ? '1px' : '50px',
        background: colors.border,
      },
      
      heroImageContainer: {
        position: 'relative', padding: '20px',
      },
      
      imageBackground: {
        position: 'absolute', top: '20px', left: '20px', right: '-20px', bottom: '-20px',
        border: `1px solid ${colors.beigeDark}`,
        borderRadius: '2px', zIndex: -1,
      },
      
      imageWrapper: {
        position: 'relative',
        height: isMobile ? '400px' : isTablet ? '500px' : '650px',
        borderRadius: '2px', overflow: 'hidden',
      },
      
      image: {
        width: '100%', height: '100%', objectFit: 'cover',
        filter: 'grayscale(25%) contrast(1.1) brightness(0.85)',
      },
      
      imageOverlay: {
        position: 'absolute', inset: 0,
        background: `linear-gradient(to top, rgba(18,18,20,0.9) 0%, transparent 50%)`,
      },
      
      imageBadge: {
        position: 'absolute', bottom: '0', left: '0',
        background: colors.bgMedium,
        padding: '1.5rem 2rem',
        borderTop: `1px solid ${colors.border}`,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex', alignItems: 'center', gap: '1.2rem', zIndex: 2,
      },
      
      badgeText: {
        display: 'flex', flexDirection: 'column', gap: '0.2rem',
      },
      
      badgeTitle: {
        color: colors.beigeMain, fontSize: '1rem', fontWeight: 400, fontFamily: "'Playfair Display', serif",
      },
      
      badgeSubtitle: {
        color: colors.textGray, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px',
      },
      
      // Services Section
      servicesSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgDark,
      },
      
      sectionHeader: {
        textAlign: 'center', marginBottom: isMobile ? '3rem' : '5rem',
      },
      
      sectionTitle: {
        fontSize: isMobile ? '2rem' : '3rem',
        fontWeight: 300, color: colors.beigeLight,
        marginBottom: '1rem', letterSpacing: '-0.5px',
      },
      
      sectionSubtitle: {
        fontSize: '1.1rem', color: colors.textGray,
        maxWidth: '600px', margin: '0 auto', fontWeight: 300,
      },
      
      servicesGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '2rem', maxWidth: '1200px', margin: '0 auto',
      },
      
      serviceCard: {
        background: colors.bgMedium,
        borderRadius: '2px',
        padding: isMobile ? '2rem' : '3rem',
        border: `1px solid ${colors.border}`,
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        display: 'flex', flexDirection: 'column', height: '100%',
      },
      
      serviceIconContainer: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: `1px solid ${colors.border}`,
      },
      
      serviceNumber: {
        fontSize: '1.5rem', color: colors.beigeMain, fontFamily: "'Playfair Display', serif", fontWeight: 300,
      },
      
      serviceTitle: {
        fontSize: '1.3rem', fontWeight: 400, color: colors.beigeLight, marginBottom: '1rem',
      },
      
      serviceDescription: {
        fontSize: '0.95rem', color: colors.textGray, lineHeight: 1.6, marginBottom: '2rem', flex: 1, fontWeight: 300,
      },
      
      serviceDetails: {
        listStyle: 'none', padding: 0, margin: 0,
        display: 'flex', flexDirection: 'column', gap: '0.8rem',
      },
      
      serviceDetail: {
        fontSize: '0.9rem', color: colors.beigeLight,
        display: 'flex', alignItems: 'center', gap: '0.8rem', fontWeight: 300,
      },
      
      detailDot: {
        width: '12px', height: '1px', backgroundColor: colors.beigeDark, flexShrink: 0,
      },
      
      // Expertise Section
      expertiseSection: {
        position: 'relative', zIndex: 2,
        padding: isMobile ? '4rem 1.5rem' : '8rem 5%',
        background: colors.bgLight,
        borderTop: `1px solid ${colors.border}`,
      },
      
      expertiseGrid: {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: '2rem', maxWidth: '1200px', margin: '0 auto',
      },
      
      expertiseArea: {
        background: colors.bgMedium,
        borderRadius: '2px', padding: '2.5rem',
        border: `1px solid ${colors.border}`,
        display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
      },
      
      areaHeader: {
        marginBottom: '2rem', zIndex: 2,
      },
      
      areaTitle: {
        fontSize: '1.2rem', fontWeight: 400, color: colors.beigeLight, marginTop: '1rem',
      },
      
      areaStats: {
        position: 'absolute', top: '1rem', right: '1.5rem',
        fontSize: '2.5rem', fontWeight: 300, color: colors.beigeMain, opacity: 0.15,
        fontFamily: "'Playfair Display', serif",
      },
      
      areaList: {
        listStyle: 'none', padding: 0, margin: 0, flex: 1,
        display: 'flex', flexDirection: 'column', gap: '1rem',
      },
      
      areaListItem: {
        color: colors.textGray, display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
        fontSize: '0.9rem', lineHeight: 1.6, fontWeight: 300,
      },
    };
  }, [isMobile, isTablet]);

  // Animations Premium (Bézier Curve)
  const premiumTransition = { duration: 1, ease: [0.16, 1, 0.3, 1] };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2, delayChildren: 0.1 } }
  };

  const fadeUpVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: premiumTransition }
  };

  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 800);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Droit de l'Environnement & Hydrocarbures | KMJ & Associés</title>
        <meta name="description" content="Expertise en droit environnemental et des hydrocarbures : autorisations, études d'impact, conformité réglementaire, contentieux." />
      </Helmet>

      <motion.section 
        style={computedStyles.section}
        initial="hidden" animate="visible" variants={containerVariants}
      >
        {/* Background Subtle Overlay */}
        <div style={computedStyles.animatedBackground}>
          <div style={computedStyles.gradientSphere} />
          <div style={computedStyles.noiseOverlay} />
        </div>

        {/* Hero Section */}
        <motion.div style={computedStyles.hero} variants={containerVariants}>
          <div style={computedStyles.heroContent}>
            <motion.div variants={fadeUpVariants}>
              <div style={computedStyles.titleLine} />
              <motion.h1 style={computedStyles.mainTitle}>
                Droit de l'environnement
                <br />
                <span style={computedStyles.titleHighlight}>& hydrocarbures.</span>
              </motion.h1>
              <motion.span style={computedStyles.subtitle}>
                Protection & Conformité réglementaire.
              </motion.span>

              <motion.div style={computedStyles.heroTextWrapper}>
                <div style={computedStyles.textCard}>
                  <div style={computedStyles.cardIcon}>🌱</div>
                  <p style={computedStyles.heroText}>
                    Le cabinet KMJ & Associés intervient en droit de l'environnement pour accompagner 
                    les entreprises opérant dans les secteurs extractifs, énergétiques et industriels.
                  </p>
                </div>
                <div style={computedStyles.textCard}>
                  <div style={computedStyles.cardIcon}>🛢️</div>
                  <p style={computedStyles.heroText}>
                    Nous conseillons nos clients sur les aspects réglementaires liés à l'exploration, 
                    l'exploitation, et le transport des hydrocarbures, en garantissant la conformité environnementale.
                  </p>
                </div>
              </motion.div>

              {/* Stats Premium (Format Document/Ligne) */}
              <motion.div style={computedStyles.statsContainer}>
                <div style={computedStyles.statItem}>
                  <motion.div 
                    style={computedStyles.statNumber}
                    initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1 }}
                  >
                    100+
                  </motion.div>
                  <div style={computedStyles.statLabel}>Projets opérés</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statItem}>
                  <motion.div 
                    style={computedStyles.statNumber}
                    initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1, delay: 0.2 }}
                  >
                    50+
                  </motion.div>
                  <div style={computedStyles.statLabel}>Autorisations</div>
                </div>
                <div style={computedStyles.statDivider} />
                <div style={computedStyles.statItem}>
                  <motion.div 
                    style={computedStyles.statNumber}
                    initial={{ opacity: 0 }} animate={{ opacity: showStats ? 1 : 0 }} transition={{ duration: 1, delay: 0.4 }}
                  >
                    15
                  </motion.div>
                  <div style={computedStyles.statLabel}>Années d'expertise</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Hero Image & Offset Frame */}
            <motion.div style={computedStyles.heroImageContainer} variants={fadeUpVariants}>
              <div style={computedStyles.imageBackground} />
              <div style={computedStyles.imageWrapper}>
                <img src="/images/L8.avif" alt="Droit de l'environnement" style={computedStyles.image} loading="lazy" />
                <div style={computedStyles.imageOverlay} />
                <div style={computedStyles.imageBadge}>
                  <div style={computedStyles.cardIcon}>⚖️</div>
                  <div style={computedStyles.badgeText}>
                    <div style={computedStyles.badgeTitle}>Excellence Juridique</div>
                    <div style={computedStyles.badgeSubtitle}>Développement durable</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Services Section */}
        <div style={computedStyles.servicesSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Nos <span style={computedStyles.titleHighlight}>Services</span> Spécialisés
            </motion.h2>
            <motion.p style={computedStyles.sectionSubtitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Une expertise complète pour sécuriser vos projets d'envergure.
            </motion.p>
          </div>

          <div style={computedStyles.servicesGrid}>
            {services.map((service, index) => (
              <motion.div
                key={service.id} style={computedStyles.serviceCard}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
              >
                <div style={computedStyles.serviceIconContainer}>
                  <div style={computedStyles.cardIcon}>{service.icon}</div>
                  <div style={computedStyles.serviceNumber}>0{service.id}</div>
                </div>
                <h3 style={computedStyles.serviceTitle}>{service.title}</h3>
                <p style={computedStyles.serviceDescription}>{service.description}</p>
                
                <ul style={computedStyles.serviceDetails}>
                  {service.details.map((detail, i) => (
                    <li key={i} style={computedStyles.serviceDetail}>
                      <div style={computedStyles.detailDot} />
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Expertise Areas */}
        <div style={computedStyles.expertiseSection}>
          <div style={computedStyles.sectionHeader}>
            <motion.h2 style={computedStyles.sectionTitle} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUpVariants}>
              Domaines d'<span style={computedStyles.titleHighlight}>Expertise</span>
            </motion.h2>
          </div>

          <div style={computedStyles.expertiseGrid}>
            {expertiseAreas.map((area, index) => (
              <motion.div
                key={index} style={computedStyles.expertiseArea}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { ...premiumTransition, delay: index * 0.1 } } }}
                whileHover={{ y: -5, borderColor: colors.beigeDark }}
              >
                <div style={computedStyles.areaHeader}>
                  <div style={computedStyles.cardIcon}>{area.icon}</div>
                  <h3 style={computedStyles.areaTitle}>{area.title}</h3>
                  <div style={computedStyles.areaStats}>{area.stats}</div>
                </div>
                
                <ul style={computedStyles.areaList}>
                  {area.points.map((point, i) => (
                    <li key={i} style={computedStyles.areaListItem}>
                      <div style={computedStyles.detailDot} />
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

      </motion.section>
    </>
  );
};

export default DroitEnvironnement;